import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mustChangePassword, setMustChangePassword] = useState(
    localStorage.getItem('must_change_password') === '1'
  );

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // Token ungültig/abgelaufen -> still entfernen, KEIN Fehler an User
      // (User landet einfach auf Login-Seite ohne irreführende Meldung)
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const newToken = response.data.access_token;
    const mustChange = !!response.data.must_change_password;
    localStorage.setItem('token', newToken);
    if (mustChange) {
      localStorage.setItem('must_change_password', '1');
    } else {
      localStorage.removeItem('must_change_password');
    }
    setToken(newToken);
    setMustChangePassword(mustChange);

    // Fetch user data
    const userResponse = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setUser(userResponse.data);
    return { ...userResponse.data, mustChangePassword: mustChange };
  };

  const changePassword = async (currentPassword, newPassword) => {
    const res = await axios.post(
      `${API_URL}/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = res.data.access_token;
    localStorage.setItem('token', newToken);
    localStorage.removeItem('must_change_password');
    setToken(newToken);
    setMustChangePassword(false);
    return res.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const newToken = response.data.access_token;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    
    // Fetch user data
    const userResponse = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setUser(userResponse.data);
    return userResponse.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('must_change_password');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      changePassword,
      mustChangePassword,
      isAdmin,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};
